import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNoindexNofollow } from '../hooks/useNoindex';


export default function AuthPage() {
    useNoindexNofollow();
    const { configEnabled, isAuthenticated, requestCode, verifyCode } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState('email');       // email | code
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [err, setErr] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendIn, setResendIn] = useState(0);

    // Куда вернуться после входа (если перешли с защищённой страницы)
    const redirectTo = location.state?.from || '/account';

    // Если уже вошли — сразу на /account
    useEffect(() => {
        if (isAuthenticated) navigate(redirectTo, { replace: true });
    }, [isAuthenticated, navigate, redirectTo]);

    // Таймер «отправить повторно через N сек»
    useEffect(() => {
        if (resendIn <= 0) return;
        const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
        return () => clearTimeout(t);
    }, [resendIn]);

    if (!configEnabled) {
        return (
            <div className="max-w-md mx-auto py-20 px-4 text-center">
                <p className="text-gray-500">Личный кабинет пока недоступен.</p>
                <Link to="/" className="btn-outline inline-block mt-4">На главную</Link>
            </div>
        );
    }

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setErr(''); setInfo('');
        const trimmed = email.trim().toLowerCase();
        if (!trimmed.includes('@')) { setErr('Введите корректный email'); return; }
        setLoading(true);
        try {
            await requestCode(trimmed);
            setStep('code');
            setResendIn(60);
            setInfo('Мы отправили код на ' + trimmed);
        } catch (e) {
            setErr(e.message || 'Не удалось отправить код');
        } finally {
            setLoading(false);
        }
    };

    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setErr('');
        const trimmed = code.trim();
        if (trimmed.length < 4) { setErr('Введите код из письма'); return; }
        setLoading(true);
        try {
            await verifyCode(email.trim().toLowerCase(), trimmed);
            // редирект сделает useEffect выше
        } catch (e) {
            setErr(e.message || 'Неверный код');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendIn > 0) return;
        setErr(''); setInfo('');
        setLoading(true);
        try {
            await requestCode(email.trim().toLowerCase());
            setResendIn(60);
            setInfo('Код отправлен повторно');
        } catch (e) {
            setErr(e.message || 'Не удалось отправить код');
        } finally {
            setLoading(false);
        }
    };

    const inputCls = 'w-full p-3 border border-[#E8E3DD] rounded-lg text-sm focus:outline-none focus:border-[#6B7A6A] transition';

    return (
        <div className="max-w-md mx-auto py-16 px-4">
            <div className="bg-white rounded-3xl border border-[#E8E3DD] shadow-xl p-8">
                <h1 className="serif text-2xl mb-1">Вход в личный кабинет</h1>
                <p className="text-xs text-gray-500 mb-6">
                    Без пароля — пришлём код на email
                </p>

                {step === 'email' && (
                    <form onSubmit={handleEmailSubmit} className="space-y-3">
                        <label className="block text-xs font-medium text-gray-600">
                            Email
                        </label>
                        <input
                            type="email"
                            autoFocus
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            className={inputCls}
                        />
                        {err && <p className="text-red-500 text-sm">{err}</p>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full py-3 rounded-lg disabled:opacity-50"
                        >
                            {loading ? 'Отправляю…' : 'Получить код'}
                        </button>
                        {/* Предупреждение для mail.ru-адресов */}
                        {/@(mail|inbox|list|bk)\.ru$/i.test(email.trim()) && (
                            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs leading-relaxed">
                                ⚠️ Письма на mail.ru могут задерживаться до 15 минут или попадать в папку «Спам» / «Рассылки». Если код не пришёл — проверьте эти папки.
                            </div>
                        )}
                        <p className="text-xs text-gray-400 text-center pt-2">
                            Продолжая, вы соглашаетесь с{' '}
                            <Link to="/privacy" className="underline text-[#6B7A6A]">
                                политикой обработки ПДн
                            </Link>
                        </p>
                    </form>
                )}

                {step === 'code' && (
                    <form onSubmit={handleCodeSubmit} className="space-y-3">
                        <p className="text-sm text-gray-600">
                            Код отправлен на <strong>{email}</strong>
                        </p>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoFocus
                            maxLength={10}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="123456"
                            className={`${inputCls} text-center text-2xl tracking-[8px] font-mono`}
                        />
                        {err && <p className="text-red-500 text-sm">{err}</p>}
                        {info && !err && <p className="text-[#6B7A6A] text-sm">{info}</p>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full py-3 rounded-lg disabled:opacity-50"
                        >
                            {loading ? 'Проверяю…' : 'Войти'}
                        </button>
                        <div className="flex justify-between text-xs pt-2">
                            <button
                                type="button"
                                onClick={() => { setStep('email'); setCode(''); setErr(''); setInfo(''); }}
                                className="text-gray-500 hover:text-[#1C1917] underline"
                            >
                                Изменить email
                            </button>
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={resendIn > 0 || loading}
                                className="text-[#6B7A6A] hover:text-[#1C1917] underline disabled:opacity-50 disabled:no-underline"
                            >
                                {resendIn > 0 ? `Отправить снова (${resendIn})` : 'Отправить снова'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}